import {
  FunctionComponent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'

import styled, { css } from 'styled-components'

import config from '@config'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import { getLocalStorageItem } from '@utils/localStorage'
import useIsInTeamPage from '@hooks/useIsInTeamPage'
import { EntityHeaderAPIProps } from '@widgets/EntityHeader'
import Popover from '@components/Popover'

import FmtOnboardingTooltipContent from './FmtOnboardingTooltipContent'

type WrapperProps = {
  fmtButtonElementTop?: number
  fmtButtonElementLeft?: number
  fmtButtonElementWidth?: number
  fmtButtonElementHeight?: number
}

type FmtTooltipTeamPageProps = {
  fmtButtonRef?: RefObject<HTMLDivElement | null>
  entityHeaderRef?: RefObject<HTMLDivElement | null>
  title: EntityHeaderAPIProps['title']
  isFollowing: boolean
}

const {
  fmtOnboarding: {
    localStorageHideFmtOnboardingTooltipKey,
    tooltipContentDesktopWidth,
    tooltipContentMobileWidth,
  },
} = config

const StyledTeamPageTooltipWrapper = styled.div<WrapperProps>`
  ${({
    theme: {
      spacing: { spacing80, spacing16, spacing56 },
    },
    fmtButtonElementTop,
    fmtButtonElementLeft,
    fmtButtonElementWidth,
    fmtButtonElementHeight,
  }) => css`
    position: absolute;
    top: calc(
      ${fmtButtonElementTop}px - ${spacing80} + ${fmtButtonElementHeight}px /
        2 + ${spacing16}
    );
    left: calc(
      ${fmtButtonElementLeft}px + ${spacing16} + ${fmtButtonElementWidth}px / 2
    );
    z-index: 3;

    ${mobileCSS(css`
      top: 128px;
      left: calc(50vw + ${spacing56});
    `)}
    ${tabletCSS(css`
      top: 112px;
      left: calc(50vw + ${spacing56});
    `)}
  `}
`
const StyledFmtTooltip = styled(Popover)<{
  left: number
  position: 'top' | 'bottom'
}>`
  ${({
    theme: {
      colors: { fillSurface },
      spacing: { spacing8, spacing16 },
    },
    position,
  }) => {
    return css`
      background-color: ${fillSurface};
      width: ${tooltipContentMobileWidth}px;
      border-radius: ${spacing8};
      ${desktopCSS(css`
        width: ${tooltipContentDesktopWidth}px;
      `)}

      ${position === 'bottom' &&
      css`
        top: ${spacing8};
        right: -110px;

        ${desktopCSS(css`
          right: calc(-${tooltipContentDesktopWidth}px / 2);
          top: ${spacing16};
        `)}
        &:after {
          right: 100px;
          ${desktopCSS(css`
            right: calc(${tooltipContentDesktopWidth}px / 2 - ${spacing8});
          `)}
        }
      `}
    `
  }}
`

const FmtTooltipTeamPage: FunctionComponent<FmtTooltipTeamPageProps> = ({
  fmtButtonRef,
  entityHeaderRef,
  title,
  isFollowing,
}) => {
  const isInTeamPage = useIsInTeamPage()
  const [fmtFollowButtonPosition, setFmtFollowButtonPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  })
  const [shouldShowTooltip, setShouldShowTooltip] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const { top, left, width, height } = fmtFollowButtonPosition

  const wraperLeft = wrapperRef?.current
    ? wrapperRef.current.getBoundingClientRect().left
    : 0

  useEffect(() => {
    if (fmtButtonRef?.current && entityHeaderRef?.current) {
      const { top, left, width, height } =
        fmtButtonRef.current.getBoundingClientRect()

      const calculatedLeft =
        left - entityHeaderRef?.current?.getBoundingClientRect().left

      setFmtFollowButtonPosition({ top, left: calculatedLeft, width, height })
    }
  }, [entityHeaderRef, fmtButtonRef])

  useEffect(() => {
    const shouldHideTooltip =
      isFollowing ||
      getLocalStorageItem(localStorageHideFmtOnboardingTooltipKey)

    const tooltipTimeout = setTimeout(() => {
      setShouldShowTooltip(!shouldHideTooltip)
    }, 3000)

    return () => clearTimeout(tooltipTimeout)
  }, [isFollowing])

  return isInTeamPage && shouldShowTooltip ? (
    <StyledTeamPageTooltipWrapper
      fmtButtonElementTop={top}
      fmtButtonElementLeft={left}
      fmtButtonElementWidth={width}
      fmtButtonElementHeight={height}
      ref={wrapperRef}
    >
      <StyledFmtTooltip position="bottom" left={wraperLeft}>
        <FmtOnboardingTooltipContent
          setShouldShowTooltip={setShouldShowTooltip}
          teamName={title}
        />
      </StyledFmtTooltip>
    </StyledTeamPageTooltipWrapper>
  ) : null
}

export default FmtTooltipTeamPage
